import { useRef, useState } from 'react';
import { useDeleteKey, usePanZoom } from '../../interactions';
import { createMindmapStore, StoreProvider, useOptionalStoreApi } from '../../store';
import { classNames } from '../../utils';
import { ConnectionLine } from '../ConnectionLine';
import { EdgeLayer } from '../EdgeRenderer';
import { NodeLayer } from '../NodeRenderer';
import { Pane } from '../Pane';
import { SelectionBox } from '../SelectionBox';
import { Viewport } from '../Viewport';
import type { CustomMindmapProps } from './CustomMindmap.types';
import { toDivProps } from './toDivProps';
import { useCanvasInit } from './useCanvasInit';
import { useControlledSync } from './useControlledSync';
import { useInteractionSync } from './useInteractionSync';
import { usePaneSize } from './usePaneSize';
import '../../styles/base.css';
import '../../styles/plugins.css';

const Canvas = (props: CustomMindmapProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<HTMLDivElement>(null);
  useControlledSync(props);
  useInteractionSync(props);
  usePaneSize(rootRef);
  usePanZoom(rendererRef);
  useDeleteKey();
  useCanvasInit(rendererRef, props.onInit);
  return (
    <div ref={rootRef} className={classNames('cm-root', props.className)} {...toDivProps(props)}>
      <div ref={rendererRef} className="cm-renderer">
        <Pane />
        <Viewport>
          <EdgeLayer />
          <NodeLayer />
          <ConnectionLine />
        </Viewport>
        <SelectionBox />
      </div>
      {props.children}
    </div>
  );
};

/**
 * The canvas. Uses the nearest `CustomMindmapProvider`'s store so hooks outside the
 * canvas see the same state (D-018); without a provider it creates and owns a store itself.
 */
export const CustomMindmap = (props: CustomMindmapProps) => {
  const parentStore = useOptionalStoreApi();
  const [ownStore] = useState(() => (parentStore ? null : createMindmapStore()));
  if (!ownStore) return <Canvas {...props} />;
  return (
    <StoreProvider value={ownStore}>
      <Canvas {...props} />
    </StoreProvider>
  );
};
